import React, { useEffect, useRef, useId } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import styles from './Modal.module.scss'

/**
 * Универсальный modal с focus-trap, ESC-close, click-outside-close, overlay.
 *
 *   <UnvModal open={open} onClose={() => setOpen(false)} title="Заголовок" size="md">
 *     <p>Содержимое</p>
 *     <UnvModal.Footer>
 *       <UnvButton variant="ghost" onClick={() => setOpen(false)}>Отмена</UnvButton>
 *       <UnvButton variant="primary" onClick={save}>Сохранить</UnvButton>
 *     </UnvModal.Footer>
 *   </UnvModal>
 */

// Глобальный счётчик открытых modal/drawer для корректного scroll-lock'а body
// при двух одновременно открытых окнах.
let openCount = 0
let prevBodyOverflow = ''
const dialogStack = []
const focusableSelector = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
const lockBodyScroll = () => {
  if (typeof document === 'undefined') return
  if (openCount === 0) {
    prevBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  openCount += 1
}
const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return
  openCount = Math.max(0, openCount - 1)
  if (openCount === 0) {
    document.body.style.overflow = prevBodyOverflow
  }
}

const UnvModal = ({
  open,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlay = true,
  showClose = true,
  className = '',
}) => {
  const { t } = useTranslation('common')
  const overlayRef = useRef(null)
  const dialogRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()

  // Стабильная ссылка на onClose, чтобы не пересобирать listener каждый рендер.
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return undefined
    const dialog = dialogRef.current
    const previousFocus = document.activeElement
    dialogStack.push(dialog)
    const onKey = (e) => {
      if (dialogStack[dialogStack.length - 1] !== dialog) return
      if (e.key === 'Escape' && onCloseRef.current) onCloseRef.current()
      if (e.key === 'Tab') {
        const controls = [...dialog.querySelectorAll(focusableSelector)].filter((control) => !control.closest('[hidden]'))
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (!first) { e.preventDefault(); dialog.focus(); return }
        if (!dialog.contains(document.activeElement) || (e.shiftKey && document.activeElement === first) || (!e.shiftKey && document.activeElement === last)) {
          e.preventDefault()
          ;(e.shiftKey ? last : first).focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    lockBodyScroll()
    // focus first focusable
    const focusTimer = setTimeout(() => {
      const focusable = dialog.querySelector('[data-autofocus]') || dialog.querySelector(focusableSelector)
      focusable?.focus()
    }, 50)
    return () => {
      document.removeEventListener('keydown', onKey)
      unlockBodyScroll()
      clearTimeout(focusTimer)
      const index = dialogStack.indexOf(dialog)
      if (index !== -1) dialogStack.splice(index, 1)
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [open])

  if (!open) return null

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === overlayRef.current && onClose) {
      onClose()
    }
  }

  const content = (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`${styles.dialog} ${styles[`s_${size}`]} ${className}`}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {(title || showClose) && (
          <div className={styles.header}>
            {title && <h3 id={titleId} className={styles.title}>{title}</h3>}
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className={styles.closeBtn}
                aria-label={t('auto.unvui_modal.close', 'Закрыть')}
              >
                <Icon name="x" size={18} />
              </button>
            )}
          </div>
        )}
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content
}

const Footer = ({ children, align = 'right' }) => (
  <div className={`${styles.footer} ${styles[`align_${align}`] || ''}`}>{children}</div>
)

UnvModal.Footer = Footer

export default UnvModal
